import React, { useContext, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import performRequest from "network/perform-request";
import { downloadAuthenticatedFile } from "network/download-authenticated-file";
import {
  organizationMembershipImportCommitRoute,
  organizationMembershipImportExportRoute,
  organizationMembershipImportPreviewRoute,
  organizationMembershipImportTemplateRoute,
} from "network/api-routes";

/**
 * CSV import / export for organization afiliaciones.
 * Parent should pass amountsConfigured (saved plan amount > 0) and onCommitted to refresh overview + payments.
 */
const OrganizationMembershipImportSection = ({ amountsConfigured = false, onCommitted }) => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const { user, selectedOrganization } = useContext(UserContext);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [commitResult, setCommitResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [destructive, setDestructive] = useState(false);
  const [destructiveDialogOpen, setDestructiveDialogOpen] = useState(false);

  const organizationId = selectedOrganization?.id;
  const validRows = preview?.summary?.valid_rows || 0;
  const canCommit = Boolean(amountsConfigured) && Boolean(preview) && validRows > 0 && !loading;

  if (!selectedOrganization) return null;

  const downloadTemplate = () => {
    downloadAuthenticatedFile({
      url: organizationMembershipImportTemplateRoute(organizationId),
      user,
      filename: "membership-import-template.csv",
      accept: "text/csv",
    }).catch(() =>
      enqueueSnackbar(intl.formatMessage({ id: "org.membership.import.error.template" }), { variant: "error" }),
    );
  };

  const downloadExport = () => {
    downloadAuthenticatedFile({
      url: organizationMembershipImportExportRoute(organizationId),
      user,
      filename: `membership-export-${selectedOrganization.slug || organizationId}.csv`,
      accept: "text/csv",
    }).catch(() =>
      enqueueSnackbar(intl.formatMessage({ id: "org.membership.import.error.export" }), { variant: "error" }),
    );
  };

  const handleFileChange = (event) => {
    const next = event.target.files?.[0];
    setFile(next || null);
    setPreview(null);
    setCommitResult(null);
    event.target.value = "";
  };

  const runPreview = () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    setLoading(true);
    performRequest(
      "post",
      organizationMembershipImportPreviewRoute(organizationId),
      formData,
      true,
      user,
      null,
      { "Content-Type": "multipart/form-data" },
    )
      .then((res) => setPreview(res.data))
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "org.membership.import.error.preview" }),
          { variant: "error" },
        ),
      )
      .finally(() => setLoading(false));
  };

  const runCommit = () => {
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    formData.append("destructive", destructive ? "1" : "0");
    setLoading(true);
    performRequest(
      "post",
      organizationMembershipImportCommitRoute(organizationId),
      formData,
      true,
      user,
      null,
      { "Content-Type": "multipart/form-data" },
    )
      .then((res) => {
        const data = res.data;
        setCommitResult(data);
        const hasErrors = Boolean(data?.errors?.length);
        if (hasErrors) {
          enqueueSnackbar(
            intl.formatMessage(
              { id: "org.membership.import.commitPartial" },
              { count: data.errors.length },
            ),
            { variant: "warning" },
          );
        } else {
          enqueueSnackbar(intl.formatMessage({ id: "org.membership.import.commitSuccess" }), { variant: "success" });
        }
        if (typeof onCommitted === "function") onCommitted();
      })
      .catch((error) =>
        enqueueSnackbar(
          error?.response?.data?.message || intl.formatMessage({ id: "org.membership.import.error.commit" }),
          { variant: "error" },
        ),
      )
      .finally(() => {
        setLoading(false);
        setDestructiveDialogOpen(false);
      });
  };

  const requestCommit = () => {
    if (destructive) {
      setDestructiveDialogOpen(true);
      return;
    }
    runCommit();
  };

  const renderErrorList = (errors, titleId) => {
    if (!errors?.length) return null;

    return (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        <RevaText variant="subtitle2" sx={{ mb: 0.5 }}>
          {intl.formatMessage({ id: titleId }, { count: errors.length })}
        </RevaText>
        <Box component="ul" sx={{ m: 0, pl: 2 }}>
          {errors.map((err, index) => (
            <li key={`import-err-${err.row ?? index}`}>
              {intl.formatMessage(
                { id: "org.membership.import.errors.row" },
                {
                  row: err.row ?? "—",
                  messages: Array.isArray(err.messages) ? err.messages.join(" ") : String(err.messages || ""),
                },
              )}
            </li>
          ))}
        </Box>
      </Alert>
    );
  };

  const renderPreviewTable = () => {
    const parseErrors = preview?.errors || [];
    const hasRows = Boolean(preview?.rows?.length);

    if (!hasRows) {
      return (
        <Stack spacing={1.5}>
          {renderErrorList(parseErrors, "org.membership.import.preview.parseErrors")}
          <RevaText color="text.secondary">
            {intl.formatMessage({
              id: parseErrors.length
                ? "org.membership.import.preview.emptyWithErrors"
                : "org.membership.import.preview.empty",
            })}
          </RevaText>
        </Stack>
      );
    }

    return (
      <Stack spacing={1.5}>
        {renderErrorList(parseErrors, "org.membership.import.preview.parseErrors")}
        <Box sx={{ overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{intl.formatMessage({ id: "org.membership.import.table.row" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "org.membership.import.table.action" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "org.membership.import.table.member" })}</TableCell>
                <TableCell>{intl.formatMessage({ id: "org.membership.import.table.status" })}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {preview.rows.map((row) => {
                const label =
                  row.data?.name ||
                  [row.data?.document_number, row.data?.member_number].filter(Boolean).join(" · ") ||
                  "—";
                const status =
                  row.errors?.length > 0
                    ? row.errors.join(" ")
                    : [
                        row.warnings?.length ? row.warnings.join(" ") : null,
                        row.dues_preview
                          ? intl.formatMessage(
                              { id: "org.membership.import.dues.summary" },
                              {
                                paid: row.dues_preview.paid_count,
                                pending: row.dues_preview.pending_count,
                              },
                            )
                          : intl.formatMessage({ id: "org.membership.import.row.ok" }),
                      ]
                        .filter(Boolean)
                        .join(" · ");

                return (
                  <TableRow key={`import-${row.row}`}>
                    <TableCell>{row.row}</TableCell>
                    <TableCell>{row.action}</TableCell>
                    <TableCell>{label}</TableCell>
                    <TableCell>{status}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Box>
      </Stack>
    );
  };

  return (
    <Box>
      <RevaText variant="h6" sx={{ mb: 1.5 }}>
        {intl.formatMessage({ id: "org.membership.sections.import" })}
      </RevaText>

      <Stack spacing={2}>
        <RevaText color="text.secondary">{intl.formatMessage({ id: "org.membership.import.intro" })}</RevaText>

        {!amountsConfigured && (
          <Alert severity="warning">{intl.formatMessage({ id: "org.membership.import.amountsBlocked" })}</Alert>
        )}

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadTemplate}>
            {intl.formatMessage({ id: "org.membership.import.downloadTemplate" })}
          </Button>
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={downloadExport}>
            {intl.formatMessage({ id: "org.membership.import.export" })}
          </Button>
          <input accept=".csv" id="org-membership-import-csv" type="file" hidden onChange={handleFileChange} />
          <label htmlFor="org-membership-import-csv">
            <Button variant="contained" component="span" startIcon={<UploadFileIcon />}>
              {intl.formatMessage({ id: "org.membership.import.selectCsv" })}
            </Button>
          </label>
          {file && <RevaText>{file.name}</RevaText>}
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ xs: "stretch", sm: "center" }}>
          <Button variant="outlined" disabled={!file || loading} onClick={runPreview}>
            {intl.formatMessage({ id: "org.membership.import.preview" })}
          </Button>
          <FormControlLabel
            control={<Checkbox checked={destructive} onChange={(e) => setDestructive(e.target.checked)} />}
            label={intl.formatMessage({ id: "org.membership.import.destructive" })}
          />
          <Button
            variant="contained"
            color={destructive ? "warning" : "primary"}
            disabled={!canCommit}
            onClick={requestCommit}
          >
            {intl.formatMessage({ id: "org.membership.import.commit" })}
          </Button>
        </Stack>

        {loading && <CircularProgress size={28} />}

        {preview && (
          <Paper sx={{ p: 2 }}>
            <RevaText variant="subtitle1" sx={{ mb: 1 }}>
              {intl.formatMessage(
                { id: "org.membership.import.preview.summary" },
                {
                  valid: preview.summary?.valid_rows || 0,
                  errors: preview.summary?.error_rows || 0,
                },
              )}
            </RevaText>
            {renderPreviewTable()}
          </Paper>
        )}

        {commitResult && (
          <Alert severity={commitResult.errors?.length ? "warning" : "info"}>
            <RevaText>
              {intl.formatMessage(
                { id: "org.membership.import.result" },
                {
                  processed: commitResult.processed || 0,
                  created: commitResult.memberships_created || 0,
                  updated: commitResult.memberships_updated || 0,
                  paid: commitResult.installments_paid || 0,
                },
              )}
            </RevaText>
            {commitResult.errors?.length > 0 && (
              <Box sx={{ mt: 1 }}>
                <RevaText variant="subtitle2" sx={{ mb: 0.5 }}>
                  {intl.formatMessage(
                    { id: "org.membership.import.commit.errors" },
                    { count: commitResult.errors.length },
                  )}
                </RevaText>
                <Box component="ul" sx={{ m: 0, pl: 2 }}>
                  {commitResult.errors.map((err, index) => (
                    <li key={`commit-err-${err.row ?? index}`}>
                      {intl.formatMessage(
                        { id: "org.membership.import.errors.row" },
                        {
                          row: err.row ?? "—",
                          messages: Array.isArray(err.messages)
                            ? err.messages.join(" ")
                            : String(err.messages || ""),
                        },
                      )}
                    </li>
                  ))}
                </Box>
              </Box>
            )}
          </Alert>
        )}
      </Stack>

      <Dialog open={destructiveDialogOpen} onClose={() => !loading && setDestructiveDialogOpen(false)}>
        <DialogTitle>{intl.formatMessage({ id: "org.membership.import.destructive.title" })}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.formatMessage({ id: "org.membership.import.destructive.body" })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDestructiveDialogOpen(false)} disabled={loading}>
            {intl.formatMessage({ id: "org.membership.import.destructive.cancel" })}
          </Button>
          <Button color="warning" variant="contained" onClick={runCommit} disabled={loading}>
            {intl.formatMessage({ id: "org.membership.import.destructive.confirm" })}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default OrganizationMembershipImportSection;
